import type { User } from "@supabase/supabase-js";
import { getSupabase } from "./supabaseClient";
import { addXp as addXpLocal } from "../economy/levelSystem";
import type { DebugSettings } from "../game/debugSettings";

export interface UserProfile {
  id: string;
  displayName: string;
  username: string | null;
  equippedCharacter: string | null;
  avatarUrl: string | null;
  provider: string | null;
  versusWins: number;
  versusLosses: number;
  versusPlayed: number;
  versusElo: number;
  rankedWins?: number;
  rankedLosses?: number;
  rankedGames?: number;
  rankedDisconnects?: number;
  rankedPeakRating?: number;
  rankedLastMatchAt?: number | null;
  level: number;
  totalXp: number;
  debugAdmin?: boolean;
  debugSettings?: Partial<DebugSettings> | null;
}

export interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
}

type AuthListener = (state: AuthState) => void;

export class AuthManager {
  private state: AuthState = { user: null, profile: null, loading: true };
  private listeners: AuthListener[] = [];

  hasBackend(): boolean {
    return true;
  }

  async init(): Promise<void> {
    const supabase = getSupabase();

    // Listen for future auth changes (sign-in, sign-out, token refresh).
    // Do NOT await async work here — it can deadlock getSession().
    supabase.auth.onAuthStateChange((event, session) => {
      console.info("[auth] onAuthStateChange:", event, session?.user?.id ?? "no user");
      if (event === "INITIAL_SESSION") return; // handled below via getSession()
      void this.updateFromSession(session?.user ?? null);
    });

    // Resolve the initial session (processes URL tokens if present).
    const { data } = await supabase.auth.getSession();
    console.info("[auth] getSession:", data.session?.user?.id ?? "no session");
    await this.updateFromSession(data.session?.user ?? null);
  }

  private async updateFromSession(user: User | null): Promise<void> {
    if (user) {
      const profile = await this.fetchProfile(user.id);
      console.info("[auth] profile fetched:", profile?.displayName ?? "null");
      if (profile != null) {
        try {
          localStorage.setItem("bitbeast.totalXp", String(profile.totalXp));
        } catch {
          // ignore localStorage failures
        }
      }
      this.state = { user, profile, loading: false };
    } else {
      this.state = { user: null, profile: null, loading: false };
    }
    this.notify();
  }

  private async fetchProfile(userId: string): Promise<UserProfile | null> {
    const supabase = getSupabase();
    // Use select("*") so the query works even if newer columns (username, level, total_xp) haven't been migrated yet.
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();
    if (error || data == null) {
      console.warn("[auth] fetchProfile failed:", error?.message ?? "no data");
      return null;
    }
    const localXp = (() => {
      try {
        return Number(localStorage.getItem("bitbeast.totalXp")) || 0;
      } catch {
        return 0;
      }
    })();
    const dbXp = typeof data.total_xp === "number" && Number.isFinite(data.total_xp)
      ? Math.max(0, Math.floor(data.total_xp))
      : null;
    const dbLevel = typeof data.level === "number" && Number.isFinite(data.level)
      ? Math.max(1, Math.floor(data.level))
      : null;
    const resolvedTotalXp = dbXp ?? localXp;
    const resolvedLevel = dbLevel ?? levelFromXpFallback(resolvedTotalXp);
    return {
      id: data.id,
      displayName: data.username ?? "PLAYER",
      username: data.username ?? null,
      equippedCharacter: data.equipped_character ?? null,
      avatarUrl: data.avatar_url ?? null,
      provider: data.provider ?? null,
      versusWins: data.versus_wins ?? 0,
      versusLosses: data.versus_losses ?? 0,
      versusPlayed: data.versus_played ?? 0,
      versusElo: data.versus_elo ?? 0,
      rankedWins: data.ranked_wins ?? data.versus_wins ?? 0,
      rankedLosses: data.ranked_losses ?? data.versus_losses ?? 0,
      rankedGames: data.ranked_games ?? data.versus_played ?? 0,
      rankedDisconnects: data.ranked_disconnects ?? 0,
      rankedPeakRating: data.ranked_peak_rating ?? data.versus_elo ?? 0,
      rankedLastMatchAt: typeof data.ranked_last_match_at === "string"
        ? new Date(data.ranked_last_match_at).getTime()
        : null,
      debugAdmin: data.debug_admin === true || data.is_admin === true,
      debugSettings: typeof data.debug_settings === "object" ? data.debug_settings : null,
      level: resolvedLevel,
      totalXp: resolvedTotalXp
    };
  }

  async loadAccountDebugSettings(userId: string): Promise<Partial<DebugSettings> | null> {
    const supabase = getSupabase();
    const { data, error } = await supabase
      .from("profiles")
      .select("debug_settings")
      .eq("id", userId)
      .single();
    if (error != null || data == null) {
      return null;
    }
    const raw = (data as { debug_settings?: unknown }).debug_settings;
    if (raw == null || typeof raw !== "object") {
      return null;
    }
    return raw as Partial<DebugSettings>;
  }

  async saveAccountDebugSettings(userId: string, settings: DebugSettings): Promise<boolean> {
    const supabase = getSupabase();
    const { error } = await supabase
      .from("profiles")
      .update({ debug_settings: settings, updated_at: new Date().toISOString() })
      .eq("id", userId);
    return error == null;
  }

  async signInWithEmail(email: string, password: string): Promise<{ error: string | null }> {
    const supabase = getSupabase();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      console.warn("[auth] signInWithPassword failed:", error.message);
      return { error: this.friendlyError(error.message) };
    }
    return { error: null };
  }

  async signUpWithEmail(email: string, password: string): Promise<{ error: string | null }> {
    const supabase = getSupabase();
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) {
      console.warn("[auth] signUp failed:", error.message);
      return { error: this.friendlyError(error.message) };
    }
    // If no session was created, email confirmation is required
    if (data.session == null) {
      return { error: null };
    }
    return { error: null };
  }

  private friendlyError(message: string): string {
    const m = message.toLowerCase();
    if (m.includes("invalid login credentials")) return "WRONG EMAIL OR PASSWORD";
    if (m.includes("email not confirmed")) return "CHECK EMAIL TO CONFIRM";
    if (m.includes("user already registered")) return "EMAIL ALREADY REGISTERED";
    if (m.includes("password") && m.includes("6")) return "PASSWORD MUST BE 6+ CHARS";
    if (m.includes("password")) return "PASSWORD TOO SHORT";
    if (m.includes("valid email")) return "ENTER A VALID EMAIL";
    if (m.includes("rate limit")) return "TOO MANY ATTEMPTS";
    return "SIGN IN FAILED";
  }

  async signInWithGoogle(): Promise<void> {
    await this.signInWithOAuthRedirect("google");
  }

  async signInWithDiscord(): Promise<void> {
    await this.signInWithOAuthRedirect("discord");
  }

  private async signInWithOAuthRedirect(provider: "google" | "discord"): Promise<void> {
    const supabase = getSupabase();
    const redirectTo = window.location.origin;
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo
      }
    });
    if (error) {
      console.error("[auth] signInWithOAuth failed", error);
    }
    // Browser navigates away — Supabase client auto-detects tokens on return
  }


  async setUsername(username: string): Promise<{ error: string | null }> {
    const userId = this.state.user?.id;
    if (!userId || !this.state.profile) {
      return { error: "NOT SIGNED IN" };
    }
    const trimmed = username.trim().toLowerCase();
    if (trimmed.length < 3) return { error: "TOO SHORT (3-16 CHARS)" };
    if (trimmed.length > 16) return { error: "TOO LONG (3-16 CHARS)" };
    if (!/^[a-z0-9_]+$/.test(trimmed)) return { error: "LETTERS, NUMBERS & _ ONLY" };

    const supabase = getSupabase();
    const { error } = await supabase
      .from("profiles")
      .update({ username: trimmed, updated_at: new Date().toISOString() })
      .eq("id", userId);
    if (error) {
      if (error.message?.toLowerCase().includes("unique") || error.code === "23505") {
        return { error: "USERNAME TAKEN" };
      }
      // Column may not exist yet — fall back to local-only
      console.warn("[auth] setUsername failed, saving locally:", error.message);
    }
    this.state.profile.username = trimmed;
    this.state.profile.displayName = trimmed;
    try { localStorage.setItem("bitbeast.username", trimmed); } catch { /* ignore */ }
    this.notify();
    return { error: null };
  }

  async addXp(amount: number): Promise<{ error: string | null }> {
    const userId = this.state.user?.id;
    if (!userId || !this.state.profile) {
      return { error: "NOT SIGNED IN" };
    }
    const xpToAdd = Math.max(0, Math.floor(amount));
    const supabase = getSupabase();
    const { data, error } = await supabase.rpc("add_xp", {
      p_user_id: userId,
      p_amount: xpToAdd,
    });
    if (error || !data?.ok) {
      console.warn("[auth] addXp RPC failed, updating locally:", error?.message ?? "rpc error");
      // Fallback: update profile state locally and persist to localStorage
      const info = addXpLocal(this.state.profile.totalXp, xpToAdd);
      this.state.profile.totalXp = info.totalXp;
      this.state.profile.level = info.level;
      try { localStorage.setItem("bitbeast.totalXp", String(info.totalXp)); } catch { /* ignore */ }
      this.notify();
      return { error: null };
    }
    this.state.profile.totalXp = data.total_xp;
    this.state.profile.level = data.level;
    try { localStorage.setItem("bitbeast.totalXp", String(data.total_xp)); } catch { /* ignore */ }
    this.notify();
    return { error: null };
  }

  async refreshProfile(): Promise<UserProfile | null> {
    const userId = this.state.user?.id;
    if (userId == null) {
      return null;
    }
    const profile = await this.fetchProfile(userId);
    if (profile == null) {
      return null;
    }
    this.state = {
      ...this.state,
      profile
    };
    this.notify();
    return profile;
  }

  async signOut(): Promise<void> {
    const supabase = getSupabase();
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn("[auth] signOut error (clearing locally):", err);
    }
    // Always clear local state, even if Supabase call failed
    try {
      localStorage.removeItem("bitbeast.totalXp");
    } catch {
      // ignore localStorage failures
    }
    this.state = { user: null, profile: null, loading: false };
    this.notify();
  }

  getState(): AuthState {
    return this.state;
  }

  subscribe(listener: AuthListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }
}

function levelFromXpFallback(totalXp: number): number {
  return addXpLocal(0, totalXp).level;
}
